// Redux slice powering the standalone /devices page.
//
// Why a second slice (vs. just reusing companyDevices)?
//   - The per-company tab keys its state by `companyId`; that doesn't fit a
//     global list.
//   - We need a different fetch endpoint (GET /api/devices) and a different
//     create endpoint (POST /api/devices, no companyId).
//   - The status / config / software-update / lookup / reassign thunks
//     already live in companyDevices and don't depend on company context —
//     this page imports them directly instead of duplicating.

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import errMsg from "../../services/errMsg";
import {
  updateCompanyDevice,
  deleteCompanyDevice,
} from "../companyDevices/companyDevicesSlice";

export const fetchAllDevices = createAsyncThunk(
  "devices/fetchAll",
  async (
    { search = "", unassigned = false, type = null, page = 1, limit = 25 } = {},
    { rejectWithValue }
  ) => {
    try {
      const { data } = await api.get(`/devices`, {
        params: {
          search: search || undefined,
          unassigned: unassigned ? 1 : undefined,
          type: type || undefined,
          page,
          limit,
        },
      });
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load devices"));
    }
  }
);

export const createStandaloneDevice = createAsyncThunk(
  "devices/createStandalone",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/devices`, payload);
      return data.data.device;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to create device"));
    }
  }
);

const initialState = {
  items: [],
  // pagination mirrors the server response after each fetch (page returned,
  // limit applied, total + pages). The `page` field below is the *intent* —
  // which page the UI wants next. They're kept separate so the request
  // never refers to its own response.
  pagination: { page: 1, limit: 25, total: 0, pages: 0 },
  page: 1,
  limit: 25,
  loading: false,
  saving: false,
  search: "",
  unassignedOnly: false,
  // null = no filter (show all types). Other values map to Device.device_type
  // (1 motor, 2 tank, 3 valve, 4 flow).
  typeFilter: null,
  error: null,
};

const slice = createSlice({
  name: "devices",
  initialState,
  reducers: {
    // Every filter change resets page to 1 — otherwise narrowing the result
    // set leaves the UI on a page that no longer exists.
    setSearch(state, { payload }) {
      state.search = payload;
      state.page = 1;
    },
    setUnassignedOnly(state, { payload }) {
      state.unassignedOnly = Boolean(payload);
      state.page = 1;
    },
    setTypeFilter(state, { payload }) {
      // Coerce: null / "" / undefined → null (all). Number → keep.
      state.typeFilter = payload == null || payload === "" ? null : Number(payload);
      state.page = 1;
    },
    setPage(state, { payload }) {
      const n = Math.max(1, Number(payload) || 1);
      state.page = n;
    },
    setLimit(state, { payload }) {
      const n = Math.max(1, Number(payload) || 25);
      state.limit = n;
      state.page = 1;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchAllDevices.pending, (s) => {
      s.loading = true;
      s.error = null;
    })
      .addCase(fetchAllDevices.fulfilled, (s, a) => {
        s.loading = false;
        s.items = a.payload.items;
        s.pagination = a.payload.pagination || s.pagination;
      })
      .addCase(fetchAllDevices.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(createStandaloneDevice.pending, (s) => {
        s.saving = true;
      })
      .addCase(createStandaloneDevice.fulfilled, (s, a) => {
        s.saving = false;
        s.items = [a.payload, ...s.items];
      })
      .addCase(createStandaloneDevice.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      // Mirror updates/deletes that originated from the shared companyDevices
      // thunks so the standalone list stays in sync without a refetch.
      .addCase(updateCompanyDevice.fulfilled, (s, a) => {
        const idx = s.items.findIndex((d) => d._id === a.payload._id);
        if (idx >= 0) s.items[idx] = a.payload;
      })
      .addCase(deleteCompanyDevice.fulfilled, (s, a) => {
        s.items = s.items.filter((d) => d._id !== a.payload);
      });
  },
});

export const {
  setSearch,
  setUnassignedOnly,
  setTypeFilter,
  setPage,
  setLimit,
  clearError,
} = slice.actions;
export default slice.reducer;
